import React, {useContext, useEffect, useState} from 'react';
import {ExdsButton, ExdsLayout, ExdsLayoutItem, ExdsText,} from '@exds/core';
import {ExdsTileHeader} from '@exds/containers';
import {ExdsIconChevronRight} from '@exds/icons';
import {ExdsSpinner} from '@exds/feedback';
import {useNavigate} from 'react-router-dom';
import {AppContext} from '../../AppContext';
import {apiPost} from '../service/service';
import Footer from '../Footer/Footer';
import './LandingPage.scss';

export function isSafePath(url) {
  try {
    const parsed = new URL(url);
    const allowedDomains = ['.jpmchase.net', '.jpmchase.com'];
    const isLocal =
      parsed.hostname === 'localhost' || parsed.hostname === '127.0.0.1';
    const isAllowedDomain = allowedDomains.some(domain => parsed.hostname.endsWith(domain));
    if (isLocal) {
      return parsed.protocol === 'http:' || parsed.protocol === 'https:';
    }
    return parsed.protocol === 'https:' && isAllowedDomain;
  } catch {
    return false;
  }
}

export const LandingPage = () => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [appId, setAppId] = useState(null);
  const {domainList} = useContext(AppContext);
  const defaultDomain = domainList?.default?.[0];
  const regionEntries = Object.entries(domainList || {}).filter(([key]) => key !== 'default');
  useEffect(() => {
    setLoading(true);
    const pathSegments = window.location.pathname.split('/').filter(Boolean);
    const id = pathSegments[pathSegments.length - 1];
    setAppId(id);
    if (!id || id.trim() === "") {
      setLoading(false);
      navigate("/notfound");
    }
    setLoading(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function createCookieAndRoute(targetDomain) {
    try {
      const data = await apiPost('/api/createCookieAndGetRoute', {
        applicationId: appId,
        targetDomain
      });
      const url = data.data.url;
      const isExternal = /^https?:\/\//i.test(url);
      if (isExternal && isSafePath(url)) {
        window.location.assign(url);
      } else if (!isExternal) {
        navigate(url);
      } else {
        navigate('/err', {
          state: {
            errorCode: 1003,
            errorMessage: 'Invalid redirect URL'
          }
        });
      }
    } catch (error) {
      navigate('/err', {
        state: {
          errorCode: 1002,
          errorMessage: 'Please contact the help desk, a critical system error has occured'
        }
      });
    }
  }

  function renderDefaultAccessTile() {
    return (
      <div className="landing-default-tile">
        <ExdsLayout
          justifyContent="space-between"
          alignItems="center"
          gap="16px"
          className="landing-default-tile-layout"
        >
          <ExdsLayoutItem flexGrow={1}>
            <div className="landing-column">
              <ExdsTileHeader primaryText="Default access" />
              <ExdsText
                primitive="p"
                color="regular"
                textStyle="bodyMedium"
                className="landing-small-top-margin"
              >
                Recommended for most users.
              </ExdsText>
            </div>
          </ExdsLayoutItem>
          <ExdsLayoutItem className="landing-default-tile-action">
            <ExdsButton
              className="landing-default-signin-button"
              text="Sign in (default access)"
              icon={<ExdsIconChevronRight aria-hidden />}
              iconPosition="trailing"
              size="small"
              onClick={() => createCookieAndRoute(defaultDomain?.name)}
            />
          </ExdsLayoutItem>
        </ExdsLayout>
      </div>
    );
  }

  function renderRegionTiles() {
    return (
      <div className="landing-region-list">
        {regionEntries.map(([regionKey, regionDomains]) => (
          <div key={regionKey} className="landing-region-tile">
            <ExdsTileHeader primaryText={regionKey} />
            <ExdsLayout gap="8px" flexWrap="wrap" className="landing-region-buttons">
              {(Array.isArray(regionDomains) ? regionDomains : []).map((domain) => (
                <ExdsButton
                  key={domain.name}
                  className="landing-region-button"
                  variant="secondary"
                  text={domain.name}
                  icon={<ExdsIconChevronRight aria-hidden />}
                  iconPosition="trailing"
                  size="small"
                  onClick={() => createCookieAndRoute(domain.name)}
                />
              ))}
            </ExdsLayout>
          </div>
        ))}
      </div>
    );
  }

  return (
    <>
      {loading && <div className="loading-spinner"><ExdsSpinner accessibleText="Loading..." /></div>}

      {!loading && (
        <div className="landing-page-container">

          <ExdsLayout
            justifyContent="center"
            alignItems="flex-start"
            className="landing-main-layout"
          >
            <ExdsLayoutItem flexBasis="568px" flexShrink={1} className="landing-card-layout-item">
              <div className="landing-card">
                <div className="landing-section-header">
                  <ExdsTileHeader primaryText="Choose your sign in region" />
                </div>

                <div className="landing-card-content">
                  <ExdsText
                    primitive="p"
                    color="regular"
                    textStyle="bodyMedium"
                    className="landing-paragraph-top-margin"
                  >
                    Select the option that matches where you usually sign in. Most
                    users should choose Default access.
                  </ExdsText>

                  {renderDefaultAccessTile()}

                  {renderRegionTiles()}

                  <ExdsText
                    primitive="p"
                    color="regular"
                    textStyle="bodyMedium"
                    className="landing-paragraph-top-margin"
                  >
                    We use cookies to store your preference for future access.
                    Delete your stored cookies to see these options again.
                  </ExdsText>

                </div>

              </div>
            </ExdsLayoutItem>
          </ExdsLayout>

          <Footer />

        </div>
      )}
    </>
  );
};

export default LandingPage;
